"use client";

import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { apiPost } from "@/utils/apiClient";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import ArrowLinkButtonDesk from "../ui/ArrowLinkButtonDesk";
import ArrowLinkButtonMob from "../ui/ArrowLinkButtonMob";
import { useLoginModal } from "@/utils/useLoginModal";
import Image from "next/image";
import InformationButton from "@/components/ui/InformationButton";
import { ROOT_URL } from "@/utils/gloableVariables";

const tabs = ["Cumulative Returns", "Scheme Returns", "Category Comparison", "Discrete Returns", "Quant Measures"] as const;

type SchemeReturn = Record<string, number | string>;

type SchemeData = {
  scheme_name: string;
  url: string;
  id: number;
  nav: number | string;
  aum: number | string;
  months_return?: SchemeReturn;
  quarters_return?: SchemeReturn;
  years_return?: SchemeReturn;
  sip_return?: SchemeReturn;
  rolling_return?: SchemeReturn;
  scheme_return?: SchemeReturn;
  bench_return?: SchemeReturn;
  category_return?: SchemeReturn;
  quant_measure?: SchemeReturn;
};

type TabLabel = (typeof tabs)[number];

type TabKeyMapValue = {
  filters?: string[];
  dataKeys: string[];
};

const tabKeyMap: Record<TabLabel, TabKeyMapValue> = {
  "Discrete Returns": {
    filters: ["months_return", "quarters_return", "years_return"],
    dataKeys: ["months_return", "quarters_return", "years_return"],
  },
  "Cumulative Returns": {
    filters: ["sip_return", "rolling_return"],
    dataKeys: ["sip_return", "rolling_return"],
  },
  "Scheme Returns": {
    filters: ["scheme_return", "bench_return"],
    dataKeys: ["scheme_return", "bench_return"],
  },
  "Category Comparison": {
    dataKeys: ["category_return"],
  },
  "Quant Measures": {
    dataKeys: ["quant_measure"],
  },
};

const filterLabelMap: Record<string, string> = {
  months_return: "Months",
  quarters_return: "Quarters",
  years_return: "Years",
  sip_return: "SIP",
  rolling_return: "Rolling",
  scheme_return: "Scheme",
  bench_return: "Benchmark",
  category_return: "Category",
  quant_measure: "Quant",
};

const tabColumnMap: Record<TabLabel, Record<string, { label: string; key: string }[] | null>> = {
  "Discrete Returns": {
    months_return: [
      { label: "Jan", key: "2025Jan" },
      { label: "Feb", key: "2025Feb" },
      { label: "Mar", key: "2025Mar" },
      { label: "Apr", key: "2025Apr" },
      { label: "May", key: "2025May" },
      { label: "Jun", key: "2025Jun" },
      { label: "Jul", key: "2025Jul" },
      { label: "Aug", key: "2025Aug" },
      { label: "Sep", key: "2025Sep" },
      { label: "Oct", key: "2025Oct" },
      { label: "Nov", key: "2025Nov" },
      { label: "Dec", key: "2025Dec" },
    ],
    quarters_return: [
      { label: "Q1 2025", key: "2025Q1" },
      { label: "Q2 2025", key: "2025Q2" },
      { label: "Q3 2025", key: "2025Q3" },
      { label: "Q4 2025", key: "2025Q4" },
      { label: "Q1 2024", key: "2024Q1" },
      { label: "Q2 2024", key: "2024Q2" },
      { label: "Q3 2024", key: "2024Q3" },
      { label: "Q4 2024", key: "2024Q4" },
      { label: "Q1 2023", key: "2023Q1" },
      { label: "Q2 2023", key: "2023Q2" },
      { label: "Q3 2023", key: "2023Q3" },
      { label: "Q4 2023", key: "2023Q4" },
    ],
    years_return: [
      { label: "2025", key: "2025" },
      { label: "2024", key: "2024" },
      { label: "2023", key: "2023" },
      { label: "2022", key: "2022" },
      { label: "2021", key: "2021" },
      { label: "2020", key: "2020" },
      { label: "2019", key: "2019" },
      { label: "2018", key: "2018" },
      { label: "2017", key: "2017" },
      { label: "2016", key: "2016" },
      { label: "2015", key: "2015" },
      { label: "2014", key: "2014" },
    ],
  },

  "Cumulative Returns": {
    sip_return: [
      { label: "1Y", key: "one_year_abs" },
      { label: "2Y", key: "two_year" },
      { label: "3Y", key: "three_year" },
      { label: "5Y", key: "five_year" },
      { label: "10Y", key: "ten_year" },
      { label: "From Launch", key: "since_inception" },
    ],
    rolling_return: [
      { label: "1Y", key: "one_year_abs" },
      { label: "2Y", key: "two_year" },
      { label: "3Y", key: "three_year" },
      { label: "5Y", key: "five_year" },
    ],
  },

  "Scheme Returns": {
    scheme_return: [
      { label: "6M", key: "six_month_abs" },
      { label: "1Y", key: "one_year_abs" },
      { label: "2Y", key: "two_year" },
      { label: "3Y", key: "three_year" },
      { label: "5Y", key: "five_year" },
    ],
    bench_return: [
      { label: "6M", key: "six_month_abs" },
      { label: "1Y", key: "one_year_abs" },
      { label: "2Y", key: "two_year" },
      { label: "3Y", key: "three_year" },
      { label: "5Y", key: "five_year" },
    ],
  },

  "Category Comparison": {
    category_return: [
      { label: "6M", key: "six_month_abs" },
      { label: "1Y", key: "one_year_abs" },
      { label: "2Y", key: "two_year" },
      { label: "3Y", key: "three_year" },
      { label: "5Y", key: "five_year" },
    ],
  },

  "Quant Measures": {
    quant_measure: [
      { label: "Alpha", key: "alpha_cm_1year" },
      { label: "Beta", key: "beta_cm_1year" },
      { label: "Sharpe", key: "sharpratio_cm_3year" },
      { label: "Std Dev", key: "volatility_cm_3year" },
    ],
  },
};

const shouldShowDummyData = (tab: TabLabel, filter: string, isLoggedIn: boolean): boolean => {
  if (isLoggedIn) return false;

  if (tab === "Discrete Returns") return true;

  if (tab === "Cumulative Returns" && filter === "rolling_return") return true;

  return false;
};

type PeerFundTableProps = {
  schemeCatId: string | null;
};

type Column = {
  label: string;
  key: string;
};

export default function PeerFundsTable({ schemeCatId }: PeerFundTableProps) {
  const [activeTab, setActiveTab] = useState<TabLabel>("Cumulative Returns");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [tableData, setTableData] = useState<SchemeData[]>([]);
  const [openTabs, setOpenTabs] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const availableFilters = tabKeyMap[activeTab]?.filters || [];

  const activeDataKey = availableFilters.length > 0 ? activeFilter || availableFilters[0] : tabKeyMap[activeTab].dataKeys[0];

  const ref = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLButtonElement>(null);
  const filterRef = useRef<HTMLButtonElement>(null);

  const handleSelect = (tab: TabLabel) => {
    setActiveTab(tab);
    setActiveFilter(null);
    setOpenTabs(false);
  };

  function formatDiscreteLabel(key: string, type: string): string {
    if (type === "months_return") {
      const year = key.slice(0, 4);
      const month = key.slice(4);
      return `${month} ${year}`;
    }

    if (type === "quarters_return") {
      const year = key.slice(0, 4);
      const quarter = key.slice(4);
      return `${quarter} ${year}`;
    }

    return key;
  }

  function getDiscreteColumns(data: SchemeData[], dataKey: keyof SchemeData): Column[] {
    if (!data.length) return [];

    const dataset = data[0][dataKey];

    if (!dataset || typeof dataset !== "object") return [];

    return Object.keys(dataset).map((key) => ({
      key,
      label: formatDiscreteLabel(key, dataKey),
    }));
  }

  useEffect(() => {
    if (!schemeCatId) return;

    const fetchPeerFundsData = async () => {
      try {
        const data = await apiPost("schemeReturnDiverse", { scheme_category_id: schemeCatId });

        if (!data) throw new Error("Failed to fetch scheme pros & cons");

        setTableData(data || []);
      } catch (e) {
        console.error("Something went wrong", e);
      }
    };

    setTimeout(() => {
      fetchPeerFundsData();
    }, 300);
  }, [schemeCatId]);

  const activeColumns = activeTab === "Discrete Returns" ? getDiscreteColumns(tableData, activeDataKey as keyof SchemeData) : tabColumnMap[activeTab]?.[activeDataKey] || [];

  if (!tableData.length) return <div ref={ref} />;

  const compareUrls = tableData
    .map((item) => item?.url)
    .filter(Boolean)
    .slice(0, 4)
    .join("-vs-");

  const lockData = shouldShowDummyData(activeTab, activeDataKey, isLoggedIn);

  return (
    <>
      <section className="mb-8 xs:mb-12">
        <div className="flex items-center gap-3 mb-2 sm:mb-4">
          <SectionHeading heading="Peer Funds" />
          <InformationButton keywords="peer-funds" />
          <ArrowLinkButtonDesk href={`${ROOT_URL}mutual-fund/compare/${compareUrls}`} text="Compare Funds" />
        </div>

        <div className="bg-white rounded-4xl border border-[#E9E9EB] p-5 sm:p-6">
          <div className="flex justify-between gap-2 xxs:gap-4">
            <div className="relative w-full max-w-96">
              <button ref={tabsRef} onClick={() => setOpenTabs((prev) => !prev)} className="w-full py-2 px-2 xxs:px-3 rounded-lg text-left bg-white border border-[#bcbcbc] text-2xs xxs:text-sm font-medium text-primaryBlack transition-all duration-350 flex items-center justify-between gap-2 hover:border-primaryBlue hover:text-primaryBlue">
                {activeTab}
                <Icon.dropdownDownArrow className={`fill-primaryBlack transition duration-350 hover:fill-primaryBlue ${openTabs ? "rotate-180" : ""}`} />
              </button>
              <CustomDropdown isOpen={openTabs} onClose={() => setOpenTabs(false)} excludeRefs={[tabsRef]} className="w-full mt-2 rounded-lg shadow-md overflow-hidden z-2">
                <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                  {tabs.map((tab) => (
                    <li key={tab} onClick={() => handleSelect(tab)} className={`px-2.5 xxs:px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${activeTab === tab ? "bg-[#f0f5fd] text-primaryBlue font-medium" : "hover:bg-[#f0f5fd] text-[#333]"}`}>
                      {tab}
                    </li>
                  ))}
                </ul>
              </CustomDropdown>
            </div>

            {availableFilters.length > 0 && (
              <div className="relative w-full max-w-28 xs:max-w-35 shrink-0">
                <button ref={filterRef} onClick={() => setOpenFilters((prev) => !prev)} className="w-full py-2 px-2 xxs:px-3 rounded-lg text-left bg-white border border-[#bcbcbc] text-2xs xxs:text-sm font-medium text-primaryBlack transition-all duration-350 flex items-center justify-between gap-2 hover:border-primaryBlue hover:text-primaryBlue">
                  {filterLabelMap[activeFilter || availableFilters[0]] || activeFilter || availableFilters[0]}
                  <Icon.dropdownDownArrow className={`fill-primaryBlack transition duration-350 hover:fill-primaryBlue ${openFilters ? "rotate-180" : ""}`} />
                </button>
                <CustomDropdown isOpen={openFilters} onClose={() => setOpenFilters(false)} excludeRefs={[filterRef]} className="w-full mt-2 rounded-lg shadow-md overflow-hidden z-2">
                  <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                    {availableFilters.map((filter) => {
                      const isActive = activeFilter ? activeFilter === filter : filter === availableFilters[0];

                      return (
                        <li
                          key={filter}
                          onClick={() => {
                            setActiveFilter(filter);
                            setOpenFilters(false);
                          }}
                          className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${isActive ? "bg-[#f0f5fd] text-primaryBlue font-medium" : "hover:bg-[#f0f5fd] text-[#333]"}`}
                        >
                          {filterLabelMap[filter] || filter}{" "}
                        </li>
                      );
                    })}
                  </ul>
                </CustomDropdown>
              </div>
            )}
          </div>

          <div className={`${lockData ? "overflow-x-hidden" : "overflow-x-auto"} scrollbar-sm sm:scrollbar-lg scrollbar-cursor mt-4 relative`}>
            {lockData && (
              <div className="absolute inset-0 z-1 bg-white/80 backdrop-blur-xs flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-4">
                <div className="m-auto w-fit h-fit">
                  <Image src={"/next_assets/images/scheme-details/lock-data.webp"} alt="Login to unlock Rolling and Discrete Returns" width={133} height={133} className="object-contain mx-auto shrink-0" />
                  <button
                    onClick={() => {
                      open();
                      setModalKey(`${activeTab}-peer-funds`);
                    }}
                    className="group flex items-center gap-4 px-7 py-3 text-base leading-5 border border-primaryBlue text-primaryBlue rounded-xl bg-white hover:bg-primaryBlue hover:text-white transition duration-300 mt-5"
                  >
                    Unlock Now
                    <Icon.dropdownDownArrow className="w-3 h-3 -rotate-90 text-primaryBlue group-hover:text-white transition duration-300" />
                  </button>
                </div>
              </div>
            )}
            <table className="min-w-full text-sm text-left">
              <thead className="text-primaryBlack border-b border-[#E9E9EB] text-center whitespace-nowrap">
                <tr>
                  <th className="py-3 pr-2 sm:py-5 sm:pr-4 text-left text-sm sm:text-base font-medium">Scheme Name</th>
                  {activeColumns.map((col) => (
                    <th key={col.key} className="py-3 px-2 sm:py-5 sm:px-3.5 text-sm sm:text-base font-medium">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tableData.map((scheme, idx) => {
                  const returns = scheme?.[activeDataKey as keyof SchemeData] || {};

                  return (
                    <tr key={idx} className="border-b last:border-none text-2xs sm:text-sm text-center">
                      <td className="py-3 pr-2 sm:py-5 sm:pr-4 text-left text-primaryBlack transition duration-300 hover:text-primaryBlue" title={scheme.scheme_name}>
                        <Link href={`/mutual-fund/${scheme.url}/mso${scheme.id}`} className="line-clamp-1 w-36 sm:w-60">
                          {scheme.scheme_name}
                        </Link>
                      </td>

                      {activeColumns.map((col) => {
                        const value = (returns as SchemeReturn)?.[col.key];
                        const numericValue = typeof value === "string" ? parseFloat(value) : Number(value);
                        const isNegative = !isNaN(numericValue) && numericValue < 0;
                        const isValidNumber = !isNaN(numericValue) && value !== null && value !== undefined && value !== "";

                        const displayValue = lockData ? "0.00" : (value ?? "-");
                        const noPercentageKeys = new Set(["alpha_cm_1year", "beta_cm_1year", "sharpratio_cm_3year"]);

                        return (
                          <td key={col.key} className="py-3 px-2 sm:py-5 sm:px-3.5">
                            <div className={`flex gap-1 items-center justify-center ${isValidNumber && !lockData ? (isNegative ? "text-red-700" : "text-[#1F840D]") : "text-[#555555]"}`}>
                              {!lockData && isValidNumber && <i className={`inline-block align-middle w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent ${isNegative ? "border-t-4 border-t-red-700" : "border-b-4 border-b-[#2C841C]"}`} />}
                              {displayValue}
                              {displayValue !== "-" && !noPercentageKeys.has(col.key) && "%"}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <ArrowLinkButtonMob href={`${ROOT_URL}mutual-fund/compare/${compareUrls}`} text="Compare Funds" className="mt-5" />
      </section>
    </>
  );
}
